import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import "../../../assets/css/doctor/doctorpages/DoctorDashboard.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";
import { FiClock, FiXCircle, FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const { kycStatus, kycAttempts, kycData, therapistUser, baseUrl, logout } = useContext(AppContext);

  const isApproved = kycStatus === "approved";

  // Stats (from /doctor/Dashboard/stats)
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Today's sessions (derived from /doctor/Bookings/list)
  const [todaysSessions, setTodaysSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isApproved) {
      setStatsLoading(false);
      setSessionsLoading(false);
      return;
    }

    const token = localStorage.getItem("therapist_token");
    if (!token) {
      setStatsLoading(false);
      setSessionsLoading(false);
      return;
    }

    const authHeaders = {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    };

    // --- KPI stats ---
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/Dashboard/stats`, authHeaders);
        if (res.data.success) {
          setStats(res.data.data);
        } else {
          setStatsError("Could not load dashboard stats");
        }
      } catch (err) {
        console.log("Doctor dashboard stats fetch error:", err);
        if (err?.response?.status === 401) {
          logout();
        } else {
          setStatsError("Something went wrong while loading your stats");
        }
      } finally {
        setStatsLoading(false);
      }
    };

    // --- Today's sessions (built from the bookings list) ---
    const fetchTodaysSessions = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/Bookings/list`, authHeaders);
        const bookings = res.data?.data ?? res.data ?? [];

        const todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

        const filtered = (Array.isArray(bookings) ? bookings : [])
          .filter((b) => (b.date || b.session_date || "").slice(0, 10) === todayStr)
          .sort((a, b) =>
            (a.time || a.session_time || "").localeCompare(b.time || b.session_time || "")
          );

        setTodaysSessions(filtered);
      } catch (err) {
        console.log("Doctor bookings fetch error:", err);
        if (err?.response?.status === 401) {
          logout();
        } else {
          setSessionsError("Could not load today's sessions");
        }
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchStats();
    fetchTodaysSessions();
  }, [baseUrl, logout, isApproved]);

  const filteredSessions = todaysSessions.filter((s) => {
    const name = s.patient_name || s.patient?.name || "";
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <DoctorMasterLayout>
      <div className="dashboard-wrapper">
        <div className="dashboard-container">

          {/* Welcome Section */}
          <div className="welcome-section">
            <div className="welcome-content">
              <h1 className="welcome-title">Welcome back, {therapistUser?.full_name || "Therapist"}</h1>
              <p className="welcome-subtitle">Here's what's happening with your therapy sessions today</p>
            </div>
            {isApproved && (
              <button className="btn-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                New Session
              </button>
            )}
          </div>

          {/* ── KYC Status Banner ─────────────────────────── */}
          {kycStatus === "pending" && (
            <div style={{
              background: "#fff7ed", border: "1px solid #fdba74", borderRadius: "12px",
              padding: "16px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px"
            }}>
              <FiClock size={22} style={{ color: "#ea580c", flexShrink: 0 }} />
              <div>
                <strong style={{ color: "#9a3412" }}>Your KYC is under review</strong>
                <p style={{ margin: "2px 0 0 0", fontSize: "14px", color: "#9a3412" }}>
                  We're verifying your documents. This usually takes 24–48 hours.
                </p>
              </div>
            </div>
          )}

          {kycStatus === "rejected" && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "12px",
              padding: "16px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px"
            }}>
              <FiAlertTriangle size={22} style={{ color: "#dc2626", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <strong style={{ color: "#991b1b" }}>Your KYC was rejected</strong>
                <p style={{ margin: "2px 0 0 0", fontSize: "14px", color: "#991b1b" }}>
                  Reason: {kycData?.rejection_reason || "No reason provided."} (Attempts: {kycAttempts}/3)
                </p>
              </div>
              <Link to="/doctor/Kyc" style={{
                background: "#dc2626", color: "#fff", padding: "8px 16px", borderRadius: "8px",
                textDecoration: "none", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap"
              }}>
                Resubmit
              </Link>
            </div>
          )}

          {kycStatus === "blocked" && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "12px",
              padding: "16px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px"
            }}>
              <FiXCircle size={22} style={{ color: "#dc2626", flexShrink: 0 }} />
              <div>
                <strong style={{ color: "#991b1b" }}>Maximum KYC attempts reached</strong>
                <p style={{ margin: "2px 0 0 0", fontSize: "14px", color: "#991b1b" }}>
                  Please contact support for further assistance.
                </p>
              </div>
            </div>
          )}

          {isApproved && (
            <div style={{
              background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "12px",
              padding: "14px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px"
            }}>
              <FiCheckCircle size={20} style={{ color: "#16a34a", flexShrink: 0 }} />
              <strong style={{ color: "#166534" }}>Your KYC is verified — you have full access to your dashboard.</strong>
            </div>
          )}

          {statsError && isApproved && (
            <p style={{ color: "red", marginBottom: "12px" }}>{statsError}</p>
          )}

          {/* Stats Grid */}
          <div className="stats-grid" style={{ opacity: isApproved ? 1 : 0.4, pointerEvents: isApproved ? "auto" : "none" }}>

            {/* Stat Card 1 - Active Patients */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                {typeof stats?.active_patients_change === "number" && (
                  <div className={`stat-change ${stats.active_patients_change >= 0 ? "positive" : "negative"}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m18 15-6-6-6 6" />
                    </svg>
                    {stats.active_patients_change}%
                  </div>
                )}
              </div>
              <div className="stat-body">
                <div className="stat-value">{statsLoading ? "…" : stats?.active_patients ?? 0}</div>
                <div className="stat-label">Active Patients</div>
              </div>
            </div>

            {/* Stat Card 2 - Today's Sessions */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                {typeof stats?.todays_sessions_change === "number" && (
                  <div className={`stat-change ${stats.todays_sessions_change >= 0 ? "positive" : "negative"}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m18 15-6-6-6 6" />
                    </svg>
                    {stats.todays_sessions_change}%
                  </div>
                )}
              </div>
              <div className="stat-body">
                <div className="stat-value">
                  {sessionsLoading ? "…" : todaysSessions.length}
                </div>
                <div className="stat-label">Today's Sessions</div>
              </div>
            </div>

            {/* Stat Card 3 - Success Rate */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                {typeof stats?.success_rate_change === "number" && (
                  <div className={`stat-change ${stats.success_rate_change >= 0 ? "positive" : "negative"}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m18 15-6-6-6 6" />
                    </svg>
                    {stats.success_rate_change}%
                  </div>
                )}
              </div>
              <div className="stat-body">
                <div className="stat-value">{statsLoading ? "…" : `${stats?.success_rate ?? 0}%`}</div>
                <div className="stat-label">Success Rate</div>
              </div>
            </div>

            {/* Stat Card 4 - Avg Satisfaction */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon" style={{ backgroundColor: 'rgba(255, 107, 53, 0.1)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                {typeof stats?.avg_satisfaction_change === "number" && (
                  <div className={`stat-change ${stats.avg_satisfaction_change >= 0 ? "positive" : "negative"}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m18 15-6-6-6 6" />
                    </svg>
                    {stats.avg_satisfaction_change}%
                  </div>
                )}
              </div>
              <div className="stat-body">
                <div className="stat-value">{statsLoading ? "…" : stats?.avg_satisfaction ?? 0}</div>
                <div className="stat-label">Avg Satisfaction</div>
              </div>
            </div>

          </div>

          {/* Today's Sessions - Full Width */}
          <div className="content-card full-width-card" style={{ opacity: isApproved ? 1 : 0.4, pointerEvents: isApproved ? "auto" : "none" }}>
            <div className="card-header">
              <div className="card-header-left">
                <svg className="card-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <h2 className="card-title">Today's Therapy Sessions</h2>
              </div>
              <div className="card-actions">
                <div className="search-container">
                  <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button className="btn-filter">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="appointments-list">

                {sessionsLoading && <p>Loading today's sessions…</p>}

                {!sessionsLoading && sessionsError && (
                  <p style={{ color: "red" }}>{sessionsError}</p>
                )}

                {!sessionsLoading && !sessionsError && filteredSessions.length === 0 && (
                  <p className="appointment-info">No sessions scheduled for today.</p>
                )}

                {!sessionsLoading && !sessionsError && filteredSessions.map((session) => {
                  const time = session.time || session.session_time || "--:--";
                  const name = session.patient_name || session.patient?.name || "Unknown Patient";
                  const info = session.session_type
                    ? `${session.session_type}${session.reason ? ` • ${session.reason}` : ""}`
                    : (session.notes || "");
                  const status = (session.status || "pending").toLowerCase();

                  return (
                    <div className="appointment-item" key={session.id}>
                      <div className="appointment-time">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {time}
                      </div>
                      <div className="appointment-details">
                        <div className="patient-name">{name}</div>
                        <div className="appointment-info">{info}</div>
                      </div>
                      <span className={`status-badge status-${status}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                  );
                })}

              </div>
              <Link to="/doctordashboard/appointments" className="btn-text" style={{ marginTop: '1rem' }}>
                View All Sessions →
              </Link>
            </div>
          </div>

        </div>
      </div>
    </DoctorMasterLayout>
  );
};

export default Dashboard;